import { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * Generic hook for entity list pages (Goals, Aspirations, Lists)
 * Reduces code duplication across similar page components
 */

interface UseEntityPageOptions<T, F, S extends { field: string; direction: 'asc' | 'desc' }> {
  /** Array of entity items */
  items: T[];
  /** Function to initialize/load items */
  initializeItems: () => void;
  /** Function to set page header info */
  setPageInfo: (title: string, action?: { label: string; icon: React.ComponentType; onClick: () => void }) => void;
  /** Page title for header */
  pageTitle: string;
  /** Label for the create action button */
  actionLabel: string;
  /** Icon component for the create action button */
  actionIcon: React.ComponentType;
  /** Default filter values */
  defaultFilters: F;
  /** Default sort configuration */
  defaultSort: S;
  /** Function to filter items based on current filters */
  filterFn: (items: T[], filters: F) => T[];
  /** Function to sort items based on current sort */
  sortFn: (items: T[], sort: S) => T[];
  /** Optional: URL search params to read initial filters from */
  searchParams?: URLSearchParams;
  /** Optional: Function to parse URL params into filters */
  parseUrlFilters?: (params: URLSearchParams) => Partial<F>;
}

interface UseEntityPageReturn<T, F, S> {
  /** Current filter values */
  filters: F;
  /** Update filters */
  setFilters: React.Dispatch<React.SetStateAction<F>>;
  /** Current sort configuration */
  sort: S;
  /** Update sort */
  setSort: React.Dispatch<React.SetStateAction<S>>;
  /** Filtered and sorted items */
  filteredItems: T[];
  /** Reset filters to defaults */
  clearFilters: () => void;
  /** Whether create/edit form is open */
  isFormOpen: boolean;
  /** Set form open state */
  setIsFormOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** ID of item being edited (null for create) */
  editingId: string | null;
  /** Set editing ID */
  setEditingId: React.Dispatch<React.SetStateAction<string | null>>;
  /** Item pending delete confirmation */
  deleteConfirm: T | null;
  /** Set delete confirmation item */
  setDeleteConfirm: React.Dispatch<React.SetStateAction<T | null>>;
  /** Open create form */
  openCreateForm: () => void;
  /** Open edit form for specific item */
  openEditForm: (id: string) => void;
  /** Close form and reset editing state */
  closeForm: () => void;
  /** Check if filters are active */
  hasActiveFilters: boolean;
}

export function useEntityPage<T, F extends object, S extends { field: string; direction: 'asc' | 'desc' }>({
  items,
  initializeItems,
  setPageInfo,
  pageTitle,
  actionLabel,
  actionIcon,
  defaultFilters,
  defaultSort,
  filterFn,
  sortFn,
  searchParams,
  parseUrlFilters,
}: UseEntityPageOptions<T, F, S>): UseEntityPageReturn<T, F, S> {
  // Initialize filters from URL params or defaults
  const [filters, setFilters] = useState<F>(() => {
    if (searchParams && parseUrlFilters) {
      const urlFilters = parseUrlFilters(searchParams);
      return { ...defaultFilters, ...urlFilters };
    }
    return defaultFilters;
  });

  const [sort, setSort] = useState<S>(defaultSort);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<T | null>(null);

  // Open create form
  const openCreateForm = useCallback(() => {
    setEditingId(null);
    setIsFormOpen(true);
  }, []);

  // Open edit form for specific item
  const openEditForm = useCallback((id: string) => {
    setEditingId(id);
    setIsFormOpen(true);
  }, []);

  // Close form and reset state
  const closeForm = useCallback(() => {
    setIsFormOpen(false);
    setEditingId(null);
  }, []);

  // Set page info on mount
  useEffect(() => {
    setPageInfo(pageTitle, {
      label: actionLabel,
      icon: actionIcon,
      onClick: openCreateForm,
    });
  }, [setPageInfo, pageTitle, actionLabel, actionIcon, openCreateForm]);

  // Initialize items on mount if empty
  useEffect(() => {
    if (items.length === 0) {
      initializeItems();
    }
  }, [items.length, initializeItems]);

  // Memoized filtered and sorted items
  const filteredItems = useMemo(() => {
    const filtered = filterFn(items, filters);
    return sortFn(filtered, sort);
  }, [items, filters, sort, filterFn, sortFn]);

  // Reset filters to defaults
  const clearFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, [defaultFilters]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      const defaultValue = (defaultFilters as Record<string, unknown>)[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (typeof value === 'string') {
        return value !== '' && value !== defaultValue;
      }
      return value !== undefined && value !== defaultValue;
    });
  }, [filters, defaultFilters]);

  return {
    filters,
    setFilters,
    sort,
    setSort,
    filteredItems,
    clearFilters,
    isFormOpen,
    setIsFormOpen,
    editingId,
    setEditingId,
    deleteConfirm,
    setDeleteConfirm,
    openCreateForm,
    openEditForm,
    closeForm,
    hasActiveFilters,
  };
}

/**
 * Helper function to create a standard filter function
 * Handles common filter patterns: search, status, arrays
 */
export function createFilterFn<T, F extends Record<string, unknown>>(
  config: {
    searchFields: (keyof T)[];
    arrayFilters?: { filterKey: keyof F; itemKey: keyof T }[];
    booleanFilters?: { filterKey: keyof F; check: (item: T) => boolean }[];
  }
) {
  return (items: T[], filters: F): T[] => {
    let filtered = [...items];

    // Search filter
    const search = filters.search as string | undefined;
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter((item) =>
        config.searchFields.some((field) => {
          const value = item[field];
          return typeof value === 'string' && value.toLowerCase().includes(searchLower);
        })
      );
    }

    // Array filters (e.g., status, category)
    if (config.arrayFilters) {
      for (const { filterKey, itemKey } of config.arrayFilters) {
        const filterValue = filters[filterKey] as string[] | undefined;
        if (filterValue && filterValue.length > 0) {
          filtered = filtered.filter((item) => {
            const itemValue = item[itemKey];
            return filterValue.includes(itemValue as string);
          });
        }
      }
    }

    // Boolean filters
    if (config.booleanFilters) {
      for (const { filterKey, check } of config.booleanFilters) {
        if (filters[filterKey]) {
          filtered = filtered.filter(check);
        }
      }
    }

    return filtered;
  };
}

/**
 * Helper function to create a standard sort function
 */
export function createSortFn<T>() {
  return (items: T[], sort: { field: string; direction: 'asc' | 'desc' }): T[] => {
    return [...items].sort((a, b) => {
      let aValue: unknown = (a as Record<string, unknown>)[sort.field];
      let bValue: unknown = (b as Record<string, unknown>)[sort.field];

      // Handle dates
      if (aValue instanceof Date) aValue = aValue.getTime();
      if (bValue instanceof Date) bValue = bValue.getTime();

      // Handle date strings
      if (sort.field === 'deadline' || sort.field === 'updatedAt' || sort.field === 'createdAt') {
        aValue = aValue ? new Date(aValue as string | number | Date).getTime() : 0;
        bValue = bValue ? new Date(bValue as string | number | Date).getTime() : 0;
      }

      // Handle strings
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      // Compare - cast to comparable types
      const aFinal = (aValue === undefined || aValue === null)
        ? (sort.direction === 'asc' ? Infinity : -Infinity)
        : aValue;
      const bFinal = (bValue === undefined || bValue === null)
        ? (sort.direction === 'asc' ? Infinity : -Infinity)
        : bValue;

      // Type-safe comparison
      if (typeof aFinal === 'number' && typeof bFinal === 'number') {
        if (aFinal < bFinal) return sort.direction === 'asc' ? -1 : 1;
        if (aFinal > bFinal) return sort.direction === 'asc' ? 1 : -1;
      } else if (typeof aFinal === 'string' && typeof bFinal === 'string') {
        if (aFinal < bFinal) return sort.direction === 'asc' ? -1 : 1;
        if (aFinal > bFinal) return sort.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };
}
